import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'

type ConnectedMap = Record<string, boolean>

export default function OnboardingSourcesPage() {
  const navigate = useNavigate()
  const [open, setOpen] = useState<string | null>(null)
  const [connected, setConnected] = useState<ConnectedMap>({})
  const [ghRepo, setGhRepo] = useState('')
  const [ghBranch, setGhBranch] = useState('main')
  const [ghToken, setGhToken] = useState('')

  const toggle = (id: string) => {
    if (connected[id]) return
    setOpen(prev => prev === id ? null : id)
  }

  const connect = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (id === 'gh' && ghRepo && ghToken) {
      // Normalise "github.com/owner/repo" → "owner/repo"
      const repo = ghRepo.replace(/^(https?:\/\/)?(www\.)?github\.com\//, '')
      localStorage.setItem('skilflow_github', JSON.stringify({ repo, branch: ghBranch || 'main', token: ghToken }))
    }
    setConnected(c => ({ ...c, [id]: true }))
    setOpen(null)
  }

  return (
    <div className="min-h-screen bg-[#f5f5f4] dark:bg-[#111] flex items-center justify-center p-5">
      <div className="bg-white dark:bg-[#1e1e1e] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-xl p-9 w-full max-w-[580px]">
        <div className="mb-7"><Logo size="lg" /></div>
        <StepIndicator current={2} />

        <h1 className="text-[20px] font-medium tracking-[-0.3px] dark:text-[#f0f0ef] mb-1.5">Where are your skills?</h1>
        <p className="text-[14px] text-[#888] dark:text-[#666] mb-7 leading-relaxed">
          Connect where your skill files live. Skilflow will sync from these sources. You can connect multiple.
        </p>

        <ConnectCard
          id="gh" icon="⑂" iconBg="bg-[#f0f0ef]"
          name="GitHub" desc="Sync skills from a private or public repo"
          open={open === 'gh'} connected={connected['gh']}
          onToggle={() => toggle('gh')}
        >
          <input className="c-input" placeholder="Repository URL (e.g. github.com/you/skills-hub)"
            value={ghRepo} onChange={e => setGhRepo(e.target.value)} />
          <input className="c-input" placeholder="Branch (default: main)"
            value={ghBranch} onChange={e => setGhBranch(e.target.value)} />
          <input className="c-input" type="password" placeholder="Personal access token"
            value={ghToken} onChange={e => setGhToken(e.target.value)} />
          <p className="text-[11px] text-[#aaa] leading-relaxed mb-2">
            Generate at GitHub → Settings → Developer settings → Personal access tokens. Needs repo read access.
          </p>
          <button onClick={e => connect(e, 'gh')} className="c-save"
            disabled={!ghRepo || !ghToken} style={{ opacity: ghRepo && ghToken ? 1 : 0.5 }}>Connect</button>
        </ConnectCard>

        <ConnectCard
          id="gd" icon="▲" iconBg="bg-[#e8f4fd]"
          name="Google Drive" desc="Sync skills from a Drive folder"
          open={open === 'gd'} connected={connected['gd']}
          onToggle={() => toggle('gd')}
        >
          <button onClick={e => connect(e, 'gd')} className="w-full py-2.5 bg-white dark:bg-[#1e1e1e] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[7px] text-[14px] font-medium dark:text-[#f0f0ef] flex items-center justify-center gap-2.5 hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer mb-2">
            <GoogleIcon />
            Connect Google Drive
          </button>
          <p className="text-[11px] text-[#aaa] dark:text-[#555] leading-relaxed">
            You'll be redirected to Google to authorize access to a specific folder. Skilflow only reads the folder you choose.
          </p>
        </ConnectCard>

        <ConnectCard
          id="up" icon="↑" iconBg="bg-[#f5f0ff]"
          name="Upload files" desc="Upload .md or .txt skill files directly"
          open={open === 'up'} connected={connected['up']}
          onToggle={() => toggle('up')}
        >
          <div
            onClick={e => connect(e, 'up')}
            className="border-[1.5px] border-dashed border-[#d5d5d3] dark:border-[#333] rounded-lg p-5 text-center cursor-pointer hover:border-[#bbb] dark:hover:border-[#555] hover:bg-[#fafaf9] dark:hover:bg-[#161616] mb-2"
          >
            <div className="text-[13px] text-[#888] dark:text-[#666]">Click to upload or drag and drop</div>
            <div className="text-[11px] text-[#bbb] dark:text-[#444] mt-1">.md and .txt files · up to 50 files at once</div>
          </div>
        </ConnectCard>

        <button
          onClick={() => navigate('/onboarding/destinations')}
          className="w-full mt-3 py-2.5 bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[7px] text-[14px] font-medium hover:bg-[#333] dark:hover:bg-[#ddd] cursor-pointer"
        >
          Continue →
        </button>
        <button
          onClick={() => navigate('/onboarding/destinations')}
          className="w-full mt-2 py-2.5 bg-transparent border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[7px] text-[14px] font-medium hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer text-[#1a1a1a] dark:text-[#f0f0ef]"
        >
          Skip for now
        </button>
      </div>
    </div>
  )
}

function ConnectCard({ id, icon, iconBg, name, desc, open, connected, onToggle, children }: {
  id: string; icon: string; iconBg: string; name: string; desc: string;
  open: boolean; connected: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <div className={`border rounded-lg mb-2 overflow-hidden ${connected ? 'border-[#16a34a]' : 'border-[#e5e5e3] dark:border-[#2e2e2e]'}`}>
      <div
        onClick={onToggle}
        className="flex items-center gap-2.5 p-3.5 cursor-pointer hover:bg-[#fafaf9] dark:hover:bg-[#161616]"
      >
        <div className={`w-[34px] h-[34px] ${iconBg} rounded-[7px] flex items-center justify-center text-[16px] flex-shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium dark:text-[#f0f0ef]">{name}</div>
          <div className="text-[11px] text-[#888] dark:text-[#666] mt-0.5">{desc}</div>
        </div>
        {connected ? (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#e8f5e9] text-[#16a34a] flex-shrink-0">✓ Connected</span>
        ) : (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#f0f0ef] dark:bg-[#252525] text-[#888] dark:text-[#666] flex-shrink-0">Optional</span>
        )}
      </div>
      {open && !connected && (
        <div className="px-3.5 pb-3.5 pt-3 border-t border-[#e5e5e3] dark:border-[#2e2e2e]">
          {children}
        </div>
      )}
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

function StepIndicator({ current }: { current: number }) {
  const steps = ['Account', 'Sources', 'Destinations', 'Import']
  return (
    <div className="flex items-center justify-center mb-7">
      {steps.map((label, i) => {
        const n = i + 1
        const isDone = n < current
        const isCurrent = n === current
        return (
          <React.Fragment key={n}>
            {i > 0 && <div className="w-7 h-px bg-[#e5e5e3] dark:bg-[#2e2e2e] mx-1.5" />}
            <div className="flex items-center">
              <div className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                isDone || isCurrent ? 'bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a]' : 'bg-[#f0f0ef] dark:bg-[#252525] text-[#bbb] dark:text-[#444]'
              }`}>
                {isDone ? '✓' : n}
              </div>
              <span className={`text-[11px] ml-1.5 whitespace-nowrap ${isCurrent ? 'text-[#1a1a1a] dark:text-[#f0f0ef] font-medium' : 'text-[#bbb] dark:text-[#444]'}`}>
                {label}
              </span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

// shared input style as global for this file
const styleEl = document.createElement('style')
styleEl.textContent = `.c-input { width: 100%; padding: 7px 10px; border: 1px solid #e5e5e3; border-radius: 6px; font-size: 13px; color: #1a1a1a; background: #fff; outline: none; font-family: inherit; margin-bottom: 6px; } .c-input:focus { border-color: #bbb; } .c-input::placeholder { color: #ccc; } .c-save { padding: 5px 14px; background: #1a1a1a; color: #fff; border: none; border-radius: 5px; font-size: 12px; font-weight: 500; cursor: pointer; } .c-save:hover { background: #333; } @media (prefers-color-scheme: dark) { .c-input { background: #222; color: #f0f0ef; border-color: #2e2e2e; } .c-input::placeholder { color: #555; } .c-input:focus { border-color: #555; } .c-save { background: #f0f0ef; color: #1a1a1a; } .c-save:hover { background: #ddd; } }`
document.head.appendChild(styleEl)
